import { createControlComponent } from "@react-leaflet/core";
import L, { Control } from "leaflet";
import "leaflet/dist/leaflet.css";
import React, { FC, useEffect, useState } from "react";
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
  useMapEvents,
} from "react-leaflet";

import { useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import LightOkAndBlockMarker from "../../../assets/images/new-dashboard-icons/lightingOk.png";
import FaultMarker from "../../../assets/images/new-dashboard-icons/faultLess20.png";
import MajorFaultMarker from "../../../assets/images/new-dashboard-icons/faultGreater20.png";
import SiteMarker from "../../../assets/images/new-dashboard-icons/location-pin.png";

import {
  getAllBlocksByCode,
  getAllSitesByCode,
} from "../../../core/api/services/dashboard.services";
import SkeletonComponent from "../../../shared/custom-spinner/skeleton.component";
import { setSelectedFaultType } from "../../../store/slices/dashboard.slice";
import * as _ from "lodash";
import "../dashboard.scss";

const lightOkMarker = new L.Icon({
  iconUrl: require("../../../assets/images/new-dashboard-icons/lightingOk.png"),
  iconSize: new L.Point(48, 52),
  className: "",
});

const faultMarker = new L.Icon({
  iconUrl: require("../../../assets/images/new-dashboard-icons/faultLess20.png"),
  iconSize: new L.Point(48, 52),
  className: "",
});

const majorFaultMarker = new L.Icon({
  iconUrl: require("../../../assets/images/new-dashboard-icons/faultGreater20.png"),
  iconSize: new L.Point(55, 60),
  className: "",
});

const redMarker = new L.Icon({
  iconUrl: require("../../../assets/images/new-dashboard-icons/location-pin-red.png"),
  iconSize: new L.Point(36, 40),
  className: "leaflet-div-icon",
});

const greenMarker = new L.Icon({
  iconUrl: require("../../../assets/images/new-dashboard-icons/location-pin.png"),
  iconSize: new L.Point(24, 24),
  className: "",
});

const yellowMarker = new L.Icon({
  iconUrl: require("../../../assets/images/new-dashboard-icons/location-pin-yellow.png"),
  iconSize: new L.Point(24, 28),
  className: "",
});

export { majorFaultMarker };

export function SetViewOnClick({ coords, zoom, maxZoom }: any) {
  const map = useMap();
  useEffect(() => {
    map.setView(coords, zoom);
    map.setMaxZoom(maxZoom);
  }, [coords, zoom, maxZoom, map]);

  return null;
}

export const ZoomControl = createControlComponent(
  (props) => new Control.Zoom(props)
);

const ZoomEventListener = ({
  setZoom,
  setCenterCoords,
  markers,
  setSlectedMarker,
}: any) => {
  useMapEvents({
    zoomend: (e) => {
      // const map = e.target;
      setZoom(e.target._zoom);
      setCenterCoords(e.target._lastCenter);

      /* const bounds = map.getBounds();
      const southWest = bounds._southWest; // Object with lat and lng properties
      const northEast = bounds._northEast; // Object with lat and lng properties

      const filtered = markers.filter((marker: any) => {
        const { latitude, longitude } = marker; 
        return (
          latitude >= southWest.lat &&
          latitude <= northEast.lat &&
          longitude >= southWest.lng &&
          longitude <= northEast.lng
        );
      }); */
    },
  });

  return null;
};

type FilterComponentType = {
  selectedTcCode: any;
  selectedSiteCode: any;
  selectedFaultType: any;
  selectedBlock: any;
};

const MapComponent: FC<FilterComponentType> = ({
  selectedTcCode,
  selectedSiteCode,
  selectedFaultType,
  selectedBlock,
}) => {
  const dispatch = useDispatch();

  const [selectedSiteCodeForMap, setSelectedSiteCodeForMap] = useState("");
  const [allSites, setAllSites] = useState([] as any);
  const [allBlocks, setAllBlocks] = useState([] as any);
  const [zoom, setZoom] = useState(12);
  // const [markers, setMarkers] = useState([] as any);
  const [markerUpdated, setMarkerUpdated] = useState(0);
  const center = [1.330525, 103.874726];
  const [centerCoords, setCenterCoords] = useState([1.330525, 103.874726]);
  const [maxZoom, setMaxZoom] = useState<number>(15);

  const navigate = useNavigate();

  const { data: blockData, refetch } = useQuery({
    queryKey: ["getAllBlocksByCode", selectedSiteCodeForMap],
    queryFn: ({ signal }) => getAllBlocksByCode(selectedSiteCodeForMap, signal),
    enabled: false,
  });

  const { data: siteData, isLoading } = useQuery({
    queryKey: ["getAllSitesByCode", selectedTcCode, selectedFaultType],
    queryFn: ({ signal }) =>
      getAllSitesByCode(selectedTcCode, selectedFaultType, signal),
    enabled: !!selectedTcCode,
  });

  useEffect(() => {
    if (selectedSiteCodeForMap) {
      refetch();
      setMaxZoom(18);
    }
  }, [selectedSiteCodeForMap]);

  useEffect(() => {
    const onBlockDataLoad = async () => {
      if (allSites?.length) {
        setMarkerUpdated(markerUpdated + 1);

        if ((selectedTcCode || selectedFaultType) && !selectedSiteCodeForMap) {
          setCenterCoords([allSites[0].latitude, allSites[0].longitude]);
        } else if (selectedSiteCodeForMap) {
          const targetSite: any = allSites.find(
            (site: any) => site.siteCode === selectedSiteCodeForMap
          );

          if (targetSite) {
            setCenterCoords([targetSite.latitude, targetSite.longitude]);
            setZoom(17);
          }
        }
      }
    };

    onBlockDataLoad();
  }, [allSites]);

  useEffect(() => {
    if (siteData?.length) {
      setAllSites(siteData);
    } else {
      // setMarkers([]);
      setMarkerUpdated(markerUpdated + 1);

      setCenterCoords([1.330525, 103.874726]);
      setZoom(12);
      setAllSites([]);
    }
  }, [siteData]);

  useEffect(() => {
    if (selectedSiteCode) {
      setSelectedSiteCodeForMap(selectedSiteCode);
    } else {
      setSelectedSiteCodeForMap("");
    }
  }, [selectedSiteCode]);

  useEffect(() => {
    if (selectedBlock) {
      setMaxZoom(18);

      if (blockData?.length) {
        const targetBlock = blockData?.find(
          (block: any) => Number.parseInt(block.blockCode) === Number.parseInt(selectedBlock)
        );

        if (!targetBlock) return;

        setAllBlocks([targetBlock]);
        setCenterCoords([targetBlock.latitude, targetBlock.longitude]);
        setZoom(18);
      }
    }
  }, [selectedBlock]);

  useEffect(() => {
    if (!blockData?.length && !selectedSiteCodeForMap) return;

    const copiedBlocks = _.cloneDeep(blockData);

    setAllBlocks(copiedBlocks);
  }, [blockData]);

  useEffect(() => {
    if (!allBlocks?.length && !selectedSiteCodeForMap) return;

    const copiedSites = _.cloneDeep(allSites);

    const targetSite: any = copiedSites.find(
      (site: any) => site.siteCode === selectedSiteCodeForMap
    );

    if (targetSite) targetSite["blocks"] = allBlocks;

    setAllSites(copiedSites);
  }, [allBlocks]);

  const onZoomEnd = async () => {
    if (!allSites?.length) return;
  };

  useEffect(() => {
    onZoomEnd();
  }, [zoom]);

  const navigateToDetail = (blockCode: any) => {
    navigate(`/dashboard/${btoa(blockCode)}`);
  };

  useEffect(() => {
    localStorage.setItem("isFilteredByFaultType", "false");
    localStorage.setItem("filteredFaultType", "0");
  }, []);

  const onChangeFaultType = (value: any) => {
    if (
      localStorage.getItem("isFilteredByFaultType") === "true" &&
      localStorage.getItem("filteredFaultType") === `${value}`
    ) {
      dispatch(setSelectedFaultType(null));
      localStorage.setItem("isFilteredByFaultType", "false");
      localStorage.setItem("filteredFaultType", "0");
      return;
    }

    dispatch(setSelectedFaultType(value));

    if (localStorage.getItem("isFilteredByFaultType") === "true") {
      localStorage.setItem("isFilteredByFaultType", "false");
    } else {
      localStorage.setItem("isFilteredByFaultType", "true");
    }

    localStorage.setItem("filteredFaultType", value);
  };

  const getSiteMarkerIcon = (site: any) => {
    if (site?.alarmCount >= 20) {
      return redMarker;
    } else if (site?.alarmCount > 0 && site?.alarmCount < 20) {
      return yellowMarker;
    } else {
      return greenMarker;
    }
  }

  const getBlockMarkerIcon = (block: any) => {
    if (block?.alarmCount >= 20) {
      return majorFaultMarker;
    } else if (block?.alarmCount > 0 && block?.alarmCount < 20) {
      return faultMarker;
    } else {
      return lightOkMarker;
    }
  }

  return (
    <div>
      <SkeletonComponent
        wrapperHeight="550px"
        skeletonHeight="550px"
        width="100%"
        isLoading={isLoading}
      >
        <MapContainer
          style={{ height: "550px", width: "100wh" }}
          attributionControl={false}
          center={center as any}
          zoom={zoom}
          scrollWheelZoom={true}
          zoomControl={false}
          minZoom={12}
          maxZoom={maxZoom}
          maxBounds={[
            [1.21092, 103.60565],
            [1.47046, 104.04192],
          ]}
        >
          <ZoomControl position="topright" />

          <TileLayer
            maxZoom={19}
            maxNativeZoom={18}
            detectRetina={true}
            attribution='<img src="https://www.onemap.gov.sg/web-assets/images/logo/om_logo.png" style="height:20px;width:20px;"/>&nbsp;<a href="https://www.onemap.gov.sg/" target="_blank" rel="noopener noreferrer">OneMap</a>&nbsp;&copy;&nbsp;contributors&nbsp;&#124;&nbsp;<a href="https://www.sla.gov.sg/" target="_blank" rel="noopener noreferrer">Singapore Land Authority</a>'
            url="https://www.onemap.gov.sg/maps/tiles/Default_HD/{z}/{x}/{y}.png"
          />

          {/* "https://maps-a.onemap.sg/v3/Default/19/413251/260135.png" */}
          {/*  https://maps-b.onemap.sg/v3/Default/19/413390/260177.png */}
          {/*  https://maps-b.onemap.sg/v3/Default_HD/{z}/{x}/{y}.png */}
          {/* http://maps-{s}.onemap.sg/v3/Default_HD/{z}/{x}/{y}.png */}
          {/* https://services.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x} */}

          {markerUpdated && allSites?.length ? (
            <>
              {(selectedSiteCode
                ? allSites.filter(
                  (site: any) => site.siteCode === selectedSiteCode
                )
                : allSites
              ).map((site: any, i: any) => (
                <React.Fragment key={site?.siteCode ?? i}>
                  {site?.latitude && site?.longitude ? (
                    <>
                      <Marker
                        opacity={zoom > 14 && site?.blocks?.length > 0 ? 0 : 1}
                        key={site?.siteCode}
                        icon={getSiteMarkerIcon(site)}
                        position={[site.latitude, site.longitude]}
                        eventHandlers={{
                          click: (e) => {
                            setSelectedSiteCodeForMap(site.siteCode);
                          },
                          mouseover: (e) => {
                            if (e?.target?.getPopup()) {
                              e?.target?.openPopup();
                            }
                          },
                          mouseout: (e) => {
                            if (e?.target?.getPopup()) {
                              e?.target?.closePopup();
                            }
                          },
                        }}
                      >
                        <Popup
                          closeButton={false}
                          position={[site?.latitude, site?.longitude]}
                        >
                          <div>Site: {site.name}</div>
                          <div style={{ color: "#999" }}>
                            Click the map marker for the block views
                          </div>
                        </Popup>
                      </Marker>

                      {zoom > 14 &&
                        site.hasOwnProperty("blocks") &&
                        site?.blocks?.length ? (
                        <>
                          {site.blocks.map((block: any) => (
                            <Marker
                              opacity={
                                zoom > 14 &&
                                  site?.blocks?.length > 0 &&
                                  selectedBlock &&
                                  selectedBlock !== block?.blockCode
                                  ? 0
                                  : 1
                              }
                              key={
                                block?.blockCode ??
                                `${block?.blockName ?? "block"}-${block?.latitude ?? ""}-${block?.longitude ?? ""}`
                              }
                              icon={getBlockMarkerIcon(block)}
                              position={[block?.latitude, block?.longitude]}
                              eventHandlers={{
                                click: (e) => {
                                  navigateToDetail(block?.blockCode);
                                },
                                mouseover: (e) => {
                                  if (e?.target?.getPopup()) {
                                    e?.target?.openPopup();
                                  }
                                },
                                mouseout: (e) => {
                                  if (e?.target?.getPopup()) {
                                    e?.target?.closePopup();
                                  }
                                },
                              }}
                            >
                              <Popup
                                closeButton={false}
                                position={[block?.latitude, block?.longitude]}
                              >
                                <div>
                                  {block.blockName}, {block.street},{" "}
                                  {block.blockCode}
                                </div>

                                <div>Site: {block.siteName}</div>
                                <div>Supplier: {block.slsSupplier}</div>
                              </Popup>
                            </Marker>
                          ))}
                        </>
                      ) : null}
                    </>
                  ) : null}
                </React.Fragment>
              ))}
            </>
          ) : null}

          <SetViewOnClick
            coords={centerCoords}
            zoom={zoom}
            maxZoom={maxZoom}
            style={{ height: "550px", width: "100wh" }}
          />

          <ZoomEventListener
            setZoom={setZoom}
            setCenterCoords={setCenterCoords}
            markers={allSites}
            setSlectedMarker={setSelectedSiteCodeForMap}
          />
        </MapContainer>

        {/* map legend section */}
        <div className="maplegends" style={{ marginTop: "-15.5rem" }}>
          <div
            style={{
              paddingTop: "10px",
              backgroundColor: "rgba(248,249,250,100)",
              borderRadius: "15px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "9rem",
              }}
            >
              <div
                className="d-flex"
                style={{ marginLeft: "0.7rem", gap: "1.2rem" }}
              >
                <div>
                  <img src={SiteMarker} alt="" style={{ width: "25px" }} />
                </div>
                <div
                  style={{
                    color: "#495057",
                    fontSize: "15px",
                    fontWeight: "400",
                  }}
                  className="mt-2"
                >
                  Site
                </div>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "9rem",
              }}
            >
              <div className="d-flex" style={{ gap: "0.65rem" }}>
                <div>
                  <img src={LightOkAndBlockMarker} alt="" style={{ width: "45px" }} />
                </div>
                <div
                  style={{
                    color: "#495057",
                    fontSize: "15px",
                    fontWeight: "400",
                  }}
                  className="mt-2"
                >
                  Block
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="maplegends" style={{ marginTop: ".2rem" }}>
          <div
            style={{
              backgroundColor: "rgba(248,249,250,100)",
              borderRadius: "15px",
            }}
          >
            <button
              type="button"
              onClick={() => onChangeFaultType(1)}
              style={{
                display: "flex",
                cursor: "pointer",
                alignItems: "center",
                width: "9rem",
                marginBottom: "-10px",
                background: "none",
                border: "none",
                padding: 0,
              }}
            >
              <div className="d-flex" style={{ gap: "0.65rem" }}>
                <div>
                  <img src={LightOkAndBlockMarker} alt="" style={{ width: "45px" }} />
                </div>
                <div
                  style={{
                    color: "#495057",
                    fontSize: "15px",
                    fontWeight: "400",
                  }}
                  className="mt-2"
                >
                  Lighting OK
                </div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => onChangeFaultType(2)}
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                width: "9rem",
                marginBottom: "-10px",
                background: "none",
                border: "none",
                padding: 0,
              }}
            >
              <div className="d-flex" style={{ gap: "0.65rem" }}>
                <div>
                  <img src={FaultMarker} alt="" style={{ width: "45px" }} />
                </div>
                <div
                  style={{
                    color: "#495057",
                    fontSize: "15px",
                    fontWeight: "400",
                  }}
                  className="mt-2"
                >
                  {"Fault (<20)"}
                </div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => onChangeFaultType(3)}
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                width: "9rem",
                background: "none",
                border: "none",
                padding: 0,
              }}
            >
              <div className="d-flex">
                <div>
                  <img
                    src={MajorFaultMarker}
                    alt=""
                    style={{ width: "55px" }}
                  />
                </div>
                <div
                  style={{
                    color: "#495057",
                    fontSize: "15px",
                    fontWeight: "400",
                    marginTop: ".75rem",
                  }}
                >
                  {"Fault (>=20)"}
                </div>
              </div>
            </button>
          </div>
        </div>
      </SkeletonComponent>
    </div>
  );
};

export default React.memo(MapComponent);
